"use client";

import { AppRouterCacheProvider } from "@mui/material-nextjs/v16-appRouter";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import type { PaletteMode } from "@mui/material";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

type ColorModeContextValue = {
  mode: PaletteMode;
  toggleMode: () => void;
};

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: "dark",
  toggleMode: () => undefined
});

export function useAppColorMode() {
  return useContext(ColorModeContext);
}

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<PaletteMode>("dark");

  useEffect(() => {
    const savedMode = window.localStorage.getItem("atelier-color-mode");
    if (savedMode === "light" || savedMode === "dark") {
      const timeoutId = window.setTimeout(() => setMode(savedMode), 0);
      return () => window.clearTimeout(timeoutId);
    }
  }, []);

  const colorMode = useMemo<ColorModeContextValue>(
    () => ({
      mode,
      toggleMode: () => {
        setMode((currentMode) => {
          const nextMode = currentMode === "dark" ? "light" : "dark";
          window.localStorage.setItem("atelier-color-mode", nextMode);
          return nextMode;
        });
      }
    }),
    [mode]
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: mode === "dark" ? "#8b7cf6" : "#6356d9" },
          secondary: { main: mode === "dark" ? "#56d6ba" : "#168b74" },
          background: {
            default: mode === "dark" ? "#090b10" : "#f4f5f8",
            paper: mode === "dark" ? "#11151d" : "#ffffff"
          },
          text: {
            primary: mode === "dark" ? "#f4f6fb" : "#171923",
            secondary: mode === "dark" ? "#9ca5b6" : "#667085"
          },
          divider: mode === "dark" ? "rgba(255,255,255,0.09)" : "rgba(20,26,38,0.10)",
          success: { main: "#35c98f" },
          warning: { main: "#f2b84b" },
          error: { main: "#ef6a7b" },
          info: { main: "#65a8ff" }
        },
        typography: {
          fontFamily: "var(--font-manrope), system-ui, sans-serif",
          h1: { letterSpacing: "-0.04em" },
          h2: { letterSpacing: "-0.035em" },
          h3: { letterSpacing: "-0.03em" },
          button: { textTransform: "none", fontWeight: 750, letterSpacing: "-0.01em" }
        },
        shape: { borderRadius: 14 },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              "*": { boxSizing: "border-box" },
              html: { backgroundColor: mode === "dark" ? "#090b10" : "#f4f5f8" },
              body: {
                margin: 0,
                // Backstop for the phone-width overflow this app kept hitting: a single
                // unbreakable token (a pasted video URL, a model id, a storage path) sets
                // its container's minimum width, which on a 390px screen pushed whole rows
                // past the right edge of the viewport. "anywhere" — unlike break-word —
                // also lowers the min-content contribution, so flex/grid tracks stop being
                // sized by the longest word. Layout fixes still belong in the components;
                // this only stops one stray string from breaking a page.
                overflowWrap: "anywhere",
                backgroundImage:
                  mode === "dark"
                    ? "radial-gradient(circle at 50% -20%, rgba(112, 91, 255, 0.15), transparent 36%)"
                    : "radial-gradient(circle at 50% -20%, rgba(99, 86, 217, 0.09), transparent 38%)",
                backgroundAttachment: "fixed"
              },
              "::selection": { background: "rgba(139, 124, 246, 0.35)" }
            }
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
                borderColor: mode === "dark" ? "rgba(255,255,255,0.09)" : "rgba(20,26,38,0.09)"
              },
              outlined: {
                boxShadow: mode === "dark" ? "0 16px 44px rgba(0,0,0,0.12)" : "0 14px 36px rgba(25,31,45,0.05)"
              }
            }
          },
          MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
              root: { borderRadius: 11, minHeight: 42, paddingInline: 18 },
              containedPrimary: {
                background: "linear-gradient(135deg, #8b7cf6 0%, #6d5be8 100%)",
                color: "#fff",
                boxShadow: "0 10px 28px rgba(109,91,232,0.26)",
                "&:hover": { boxShadow: "0 14px 34px rgba(109,91,232,0.34)" }
              }
            }
          },
          MuiChip: {
            styleOverrides: {
              root: { borderRadius: 9, fontWeight: 700 },
              filled: { border: `1px solid ${mode === "dark" ? "rgba(255,255,255,.07)" : "rgba(20,26,38,.07)"}` }
            }
          },
          MuiTextField: { defaultProps: { variant: "outlined" } },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                background: mode === "dark" ? "rgba(255,255,255,0.025)" : "rgba(20,26,38,0.018)"
              }
            }
          },
          MuiTabs: { styleOverrides: { indicator: { height: 3, borderRadius: 3 } } },
          MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 750 } } },
          MuiAlert: { styleOverrides: { root: { borderRadius: 12 } } },
          MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 8, fontWeight: 650 } } }
        }
      }),
    [mode]
  );

  return (
    <AppRouterCacheProvider>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </ColorModeContext.Provider>
    </AppRouterCacheProvider>
  );
}
